import { Button } from '@react-spectrum/s2';
import { useCallback } from 'react';

import { GenericReview } from '@/components/artifact/GenericReview';
import {
	TYPE_TITLES,
	isArtifactActionable,
} from '@/components/artifact/panelMeta';
import { useJobActivation } from '@/components/artifact/useJobActivation';
import { ChatPanelLayout } from '@/spaces/chat/panels/ChatPanelLayout';

import type { Artifact } from '../../../../../bridge/types/artifact.ts';

interface GenericArtifactPanelProps {
	artifact: Artifact;
	onBack?: () => void;
	onClose: () => void;
	style?: React.CSSProperties;
}

export const GenericArtifactPanel = ({
	artifact,
	onBack,
	onClose,
	style,
}: GenericArtifactPanelProps) => {
	const title = TYPE_TITLES[artifact.type] ?? 'Review Item';
	const { handleActivate, activatedView } = useJobActivation({
		artifact,
		title: `${title} activated`,
		summary: "I'll start running this now.",
		onClose,
	});

	const renderActivateButton = useCallback(
		() => (
			<Button type="button" variant="accent" onPress={handleActivate}>
				Activate
			</Button>
		),
		[handleActivate],
	);

	const showActivateButton = isArtifactActionable(artifact);
	const actionRenderer =
		showActivateButton && !activatedView ? renderActivateButton : undefined;

	return (
		<ChatPanelLayout
			title={title}
			description={
				activatedView
					? undefined
					: artifact.user_summary || artifact.description || undefined
			}
			actionRenderer={actionRenderer}
			isLoading={false}
			onBack={onBack}
			onClose={onClose}
			style={style}
		>
			{activatedView ?? (
				<GenericReview
					content={artifact.content}
					artifactType={artifact.type}
				/>
			)}
		</ChatPanelLayout>
	);
};
